import { useAuth } from "@/auth/AuthProvider";
import { DateText } from "@/components/DateText";
import { useCarrierLogo } from "@/lib/useCarrierLogo";
import type { BrokerCarrierSummary } from "@/lib/brokerCarriers";

export function Monogram({ name }: { name: string }) {
  const initials =
    name
      .split(/[\s-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join("") || "?";
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-brk-border bg-brk-bg text-sm font-semibold text-brk-dim"
    >
      {initials}
    </span>
  );
}
function Logo({ path, name }: { path: string; name: string }) {
  const { authedBlob } = useAuth();
  const url = useCarrierLogo(authedBlob, path);
  return url ? (
    <img src={url} alt="" className="h-10 w-10 shrink-0 object-contain" />
  ) : (
    <Monogram name={name} />
  );
}
export function MarketBadge({
  type,
}: {
  type: "admitted" | "surplus_lines" | null | undefined;
}) {
  if (type !== "admitted" && type !== "surplus_lines") return null;
  return (
    <span className="inline-block rounded border border-brk-border px-2 py-0.5 text-xs text-brk-dim">
      {type === "admitted" ? "Admitted" : "Non-admitted (surplus lines)"}
    </span>
  );
}
export function CarrierIdentity({
  name,
  profile,
  nameId,
  heading = false,
}: {
  name: string | null;
  profile?: BrokerCarrierSummary | null;
  nameId?: string;
  heading?: boolean;
}) {
  const displayName = profile?.displayName ?? name ?? "Carrier";
  const rating = profile?.financialStrength;
  const Wrap = heading ? "div" : "span";
  const Name = heading ? "h2" : "span";
  return (
    <Wrap className="flex min-w-0 items-start gap-3">
      {profile?.logoUrl ? (
        <Logo key={profile.logoUrl} path={profile.logoUrl} name={displayName} />
      ) : (
        <Monogram name={displayName} />
      )}
      <Wrap className="flex min-w-0 flex-col gap-1">
        <Name
          id={nameId}
          className="min-w-0 break-words text-heading text-brk-text"
        >
          {displayName}
        </Name>
        {rating ? (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-brk-dim">
            <span>
              AM Best {rating.rating}
              {rating.financialSizeCategory
                ? ` · ${rating.financialSizeCategory}`
                : ""}
            </span>
            <span>
              as of <DateText value={rating.effectiveDate} />
            </span>
            {rating.underReview && (
              <span className="rounded border border-brk-border px-1.5">
                Under review
              </span>
            )}
          </span>
        ) : (
          profile && (
            <span className="text-xs text-brk-dim">
              No AM Best rating on file
            </span>
          )
        )}
      </Wrap>
    </Wrap>
  );
}
